/**
 * Operator-facing session player. Reached via /play/<sid>.
 *
 * Mounts `SyncedViewerPanel` against a minimal session handle so the
 * operator gets gapless playback + map / IMU / speed sync + live-mode
 * appends. The route is minimal because the synced viewer already does
 * the heavy lifting — we just hand it a `session` prop in the shape it
 * expects and the v2 master/unions via `ctx.v2`.
 *
 * `/api/fleet/sessions/<sid>` gives the authoritative `state`
 * (recording / ended / unclean); the coverage endpoint is the
 * contract-version authority. Only media-contract-v2 sessions are
 * playable, so a coverage verdict that isn't v2 renders the unavailable
 * state below instead of a player.
 *
 * App.tsx matches the route manually and passes `sessionId` as a prop
 * — same pattern as `ShareViewerPage`.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Maximize, Minimize } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../services/api';
import { FleetSessionMeta, SessionCoverage } from '../types';
import PluginSlot from '../components/PluginSlot';
import UploadTelemetryPanel from '../components/UploadTelemetryPanel';

interface PlaySessionPageProps {
  sessionId: string;
}

const v2MasterUrl = (sessionId: string) =>
  `/api/fleet/sessions/${encodeURIComponent(sessionId)}/v2/master.m3u8`;

const RETIRED_FORMAT_MESSAGE =
  'This session was recorded under the retired legacy format and can no longer be played.';

/** The minimal session handle the synced-viewer plugin needs — it only
 *  ever reads `session_id` and `is_active`; the v2 master/unions arrive
 *  separately via `ctx.v2`. */
interface SessionHandle {
  session_id: string;
  is_active: boolean;
}

const synthesiseV2Session = (sessionId: string, meta: FleetSessionMeta): SessionHandle => ({
  session_id: sessionId,
  is_active: meta.state === 'recording',
});

export const PlaySessionPage: React.FC<PlaySessionPageProps> = ({ sessionId }) => {
  const [session, setSession] = useState<SessionHandle | null>(null);
  const [meta, setMeta] = useState<FleetSessionMeta | null>(null);
  // Set only for media-contract-v2 sessions. Refreshed by the same 5 s poll as
  // the rest of the page: unions widen in place, and a snapshot_rev change
  // reloads the player's source in place too (see V2Player's snapshotRev prop).
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // A session's media contract never changes, so once coverage has answered
  // "v2" a later probe answering otherwise is a bad read, not real news.
  const isV2Ref = useRef(false);

  useEffect(() => {
    let cancelled = false;
    isV2Ref.current = false;

    const load = async () => {
      try {
        const sessionMeta = await api.getSessionMetadata(sessionId).catch(() => null);
        if (cancelled) return;
        if (!sessionMeta) {
          setError(`session ${sessionId} not found`);
          return;
        }
        setMeta(sessionMeta);
        const coverage = await api.getSessionCoverage(sessionId).catch(() => null);
        if (cancelled) return;
        if (coverage?.media_contract_version === 2) {
          isV2Ref.current = true;
          setV2Coverage(coverage);
          setSession(synthesiseV2Session(sessionId, sessionMeta));
          setError(null);
          return;
        }
        if (isV2Ref.current) {
          // A session's media contract is immutable, so only the FIRST
          // successful probe decides it. A later probe that fails, or that
          // answers "legacy" because the coverage route could not read the
          // session row and fell through, is a bad answer about a v2
          // session — not news that its media disappeared. Keep the player
          // on its stable master and let the next tick refresh the unions;
          // tearing down here reported a live session as unavailable
          // mid-playback.
          return;
        }
        setV2Coverage(null);
        setSession(null);
        setError(RETIRED_FORMAT_MESSAGE);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load session');
        }
      }
    };

    void load();
    const intervalId = window.setInterval(load, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [sessionId]);

  // The v2 master can legitimately be unservable for a while — a session that
  // is recording but has not yet composed a timeline answers 409 until its
  // first timed artifact lands. Drop back to the loading/error state so the 5 s
  // poll re-probes and remounts the player once it becomes servable: a slow,
  // visible retry rather than a permanent overlay on a stuck element.
  const handleV2FatalError = (detail: string) => {
    isV2Ref.current = false;
    setV2Coverage(null);
    setSession(null);
    setError(`v2 playback unavailable — ${detail}`);
  };

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('fullscreen failed', err);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-bg-base p-2 text-fg sm:p-4">
      <div className="mb-2 flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.history.length > 1) window.history.back();
                  else window.location.href = '/fleet';
                }}
                aria-label="Back to fleet"
              >
                <ArrowLeft />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Back to fleet</TooltipContent>
          </Tooltip>
          <span className="font-mono text-accent-purple">Map Player · {sessionId}</span>
          {meta?.state && (
            <Badge
              variant="outline"
              className={cn(
                'font-mono',
                meta.state === 'recording'
                  ? 'border-status-online text-status-online'
                  : 'border-border text-fg-secondary',
              )}
            >
              {meta.state}
            </Badge>
          )}
          {meta?.device_id && (
            <Badge variant="outline" className="font-mono text-fg-secondary">
              {meta.device_id}
            </Badge>
          )}
          <div className="flex-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize /> : <Maximize />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {error && !session && (
        <div className="mt-16 flex flex-col items-center gap-2">
          <p className="font-mono text-status-warning">{error}</p>
          {meta?.state === 'recording' && (
            <p className="text-xs text-fg-muted">
              recording is live; segments will appear here as they upload
            </p>
          )}
        </div>
      )}

      {!session && !error && (
        <div className="mt-16 flex flex-col items-center gap-3">
          <Spinner size={28} />
          <p className="text-fg-secondary">loading session…</p>
        </div>
      )}

      {session && (
        <div className="mt-2">
          <PluginSlot
            id="session.detail.panels"
            session={session}
            v2={
              v2Coverage
                ? {
                    masterUrl: v2MasterUrl(sessionId),
                    audioUnion: v2Coverage.audio_union,
                    videoUnion: v2Coverage.video_union,
                    snapshotRev: v2Coverage.snapshot_rev ?? null,
                    audioExpected: v2Coverage.audio_expected ?? null,
                    rotation: v2Coverage.rotation ?? null,
                  }
                : undefined
            }
            onV2FatalError={handleV2FatalError}
          />
          {/* Saved per-session upload-pipeline history (work-start →
              drain-complete). Renders a graceful empty state for sessions
              recorded before per-session attribution shipped. */}
          <div className="mt-3">
            <UploadTelemetryPanel sessionId={sessionId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaySessionPage;
